import React, { MouseEvent, useCallback, useMemo, useRef, useState } from 'react'
import {
    CircularProgress,
    ClickAwayListener,
    Fade,
    IconButton,
    Popper,
    Stack,
    SvgIcon,
    Typography,
} from '@mui/material'
import { ReactComponent as ActionsMenuIcon } from '../../../../../assets/images/actions/actions.svg'
import { ReactComponent as DeleteIcon } from '../../../../../assets/images/bin.svg'
import { ReactComponent as ResolveIcon } from '../../../../../assets/images/check-circle-1.svg'
import { ReactComponent as ActionIcon } from '../../../../../assets/images/actions/context.svg'
import { useEscapeKey } from '../../../../hooks/useEscapeKey'
import { Comment } from '../../Comments.types'
import { CommentActionDialog } from '../CommentActionDialog/CommentActionDialog'

type CommentActionsMenuProps = {
    comment: Comment
    canRemove: boolean
    canEditCommentMeta: boolean
    isLoading: boolean
    isRemoving: boolean
    isVisible: boolean
    onRemove: () => Promise<void>
    onToggleResolved: () => Promise<void>
    onSaveAction: (action: string) => Promise<void>
}

type ActionRow = {
    id: 'delete' | 'resolved' | 'action'
    label: string
    icon: React.ElementType
    loading?: boolean
    tone?: 'default' | 'danger'
    onClick?: (event: MouseEvent<HTMLElement>) => void | Promise<void>
}

export const CommentActionsMenu = ({
    comment,
    canRemove,
    canEditCommentMeta,
    isLoading,
    isRemoving,
    isVisible,
    onRemove,
    onToggleResolved,
    onSaveAction,
}: CommentActionsMenuProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLElement | null>(null)
    const [isActionDialogOpen, setIsActionDialogOpen] = useState(false)
    const [isActionDialogReadOnly, setIsActionDialogReadOnly] = useState(false)
    const [actionDraft, setActionDraft] = useState(comment.action ?? '')
    const hasSubmenuOpenRef = useRef(false)

    hasSubmenuOpenRef.current = !!deleteAnchorEl

    const resetActionDialogState = useCallback(() => {
        setActionDraft(comment.action ?? '')
        setIsActionDialogReadOnly(false)
        setIsActionDialogOpen(false)
    }, [comment.action])

    const handleCloseAll = useCallback(() => {
        setAnchorEl(null)
        setDeleteAnchorEl(null)
    }, [])

    useEscapeKey(handleCloseAll, !!anchorEl || !!deleteAnchorEl || isActionDialogOpen)

    const handleToggleMenu = (event: MouseEvent<HTMLElement>) => {
        if (anchorEl) {
            handleCloseAll()
            return
        }

        setAnchorEl(event.currentTarget)
    }

    const handleOpenActionDialog = (readOnly: boolean) => {
        setActionDraft(comment.action ?? '')
        setIsActionDialogReadOnly(readOnly)
        setIsActionDialogOpen(true)
        handleCloseAll()
    }

    const actions = useMemo<ActionRow[]>(() => {
        const nextActions: ActionRow[] = []

        if (canEditCommentMeta) {
            nextActions.push({
                id: 'action',
                label: comment.action ? 'Edit action' : 'Add action',
                icon: ActionIcon,
                onClick: () => handleOpenActionDialog(false),
            })
            nextActions.push({
                id: 'resolved',
                label: comment.isResolved ? 'Unmark resolved' : 'Mark resolved',
                icon: ResolveIcon,
                loading: isLoading && !isRemoving,
                onClick: async () => {
                    await onToggleResolved()
                    handleCloseAll()
                },
            })
        }

        if (canRemove) {
            nextActions.push({
                id: 'delete',
                label: 'Delete',
                icon: DeleteIcon,
                loading: isRemoving,
                tone: 'danger',
                onClick: (event) => {
                    setDeleteAnchorEl(event.currentTarget)
                },
            })
        }

        return nextActions
    }, [
        canEditCommentMeta,
        canRemove,
        comment.action,
        comment.isResolved,
        handleCloseAll,
        isActionDialogOpen,
        isLoading,
        isRemoving,
        onToggleResolved,
    ])

    if (!actions.length) {
        return null
    }

    const renderActionRow = (action: ActionRow) => (
        <Stack
            key={action.id}
            onClick={async (event) => {
                if (isLoading) {
                    return
                }

                try {
                    await action.onClick?.(event)
                } catch {}
            }}
            direction="row"
            gap={1.5}
            px={1.5}
            py={0.75}
            alignItems="center"
            sx={{
                color: action.tone === 'danger' ? 'figma.accentColors.red' : 'figma.grayscale.textHier2',
                '&:hover': {
                    color: 'figma.primary',
                },
                cursor: isLoading ? 'progress' : 'pointer',
            }}
        >
            <SvgIcon
                component={action.loading ? () => <CircularProgress size={20} color="primary" /> : action.icon}
                inheritViewBox
                fontSize="small"
                color="inherit"
                sx={{ fontSize: '20px' }}
            />
            <Typography variant="14x400" color="inherit" whiteSpace="nowrap">
                {action.label}
            </Typography>
        </Stack>
    )

    return (
        <>
            <Fade in={isVisible || !!anchorEl}>
                <Stack direction="row">
                    <IconButton
                        aria-label="Comment actions"
                        size="small"
                        onClick={handleToggleMenu}
                        disabled={isLoading}
                        sx={{
                            padding: 0.5,
                            color: 'figma.grayscale.textHier2',
                            '&:hover': {
                                color: 'primary.main',
                            },
                        }}
                    >
                        <SvgIcon component={ActionsMenuIcon} inheritViewBox fontSize="small" color="inherit" />
                    </IconButton>
                </Stack>
            </Fade>

            <Popper open={!!anchorEl} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1400 }}>
                {({ TransitionProps }) => (
                    <ClickAwayListener
                        onClickAway={() => {
                            if (hasSubmenuOpenRef.current) {
                                return
                            }

                            handleCloseAll()
                        }}
                    >
                        <Fade {...TransitionProps} in={!!anchorEl} timeout={200} unmountOnExit mountOnEnter>
                            <Stack
                                direction="column"
                                py={0.75}
                                boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.20)"
                                borderRadius="12px"
                                bgcolor="figma.grayscale.tier7"
                                ml={-1.25}
                            >
                                {actions.map(renderActionRow)}
                            </Stack>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>

            <Popper
                open={!!deleteAnchorEl}
                anchorEl={deleteAnchorEl}
                placement="right-start"
                modifiers={[
                    {
                        name: 'offset',
                        options: { offset: [-4, 0] },
                    },
                ]}
                sx={{ zIndex: 1400 }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={() => !isRemoving && setDeleteAnchorEl(null)}>
                        <Fade {...TransitionProps} in={!!deleteAnchorEl} timeout={200} unmountOnExit mountOnEnter>
                            <Stack
                                direction="column"
                                py={0.75}
                                boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.20)"
                                borderRadius="12px"
                                bgcolor="figma.grayscale.tier7"
                            >
                                <Stack
                                    direction="row"
                                    gap={1.5}
                                    px={1.5}
                                    py={0.75}
                                    alignItems="center"
                                    onClick={async () => {
                                        if (isRemoving) {
                                            return
                                        }

                                        try {
                                            await onRemove()
                                            handleCloseAll()
                                        } catch {}
                                    }}
                                    sx={{
                                        color: isRemoving ? 'figma.primary' : 'figma.accentColors.red',
                                        '&:hover': {
                                            color: 'figma.primary',
                                        },
                                        cursor: isRemoving ? 'progress' : 'pointer',
                                    }}
                                >
                                    <Typography variant="14x400" color="inherit" whiteSpace="nowrap">
                                        Confirm
                                    </Typography>
                                </Stack>
                                <Stack
                                    direction="row"
                                    gap={1.5}
                                    px={1.5}
                                    py={0.75}
                                    alignItems="center"
                                    onClick={() => {
                                        if (isRemoving) {
                                            return
                                        }

                                        setDeleteAnchorEl(null)
                                    }}
                                    sx={{
                                        color: 'figma.grayscale.textHier2',
                                        '&:hover': {
                                            color: 'figma.primary',
                                        },
                                        opacity: isRemoving ? 0.4 : 1,
                                        cursor: isRemoving ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    <Typography variant="14x400" color="inherit" whiteSpace="nowrap">
                                        Cancel
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>

            <CommentActionDialog
                open={isActionDialogOpen}
                value={actionDraft}
                isLoading={isLoading && !isRemoving}
                readOnly={isActionDialogReadOnly}
                onChange={setActionDraft}
                onClose={resetActionDialogState}
                onSave={() => {
                    if (isActionDialogReadOnly || isLoading) {
                        return
                    }
                    void (async () => {
                        try {
                            await onSaveAction(actionDraft)
                            resetActionDialogState()
                        } catch {}
                    })()
                }}
            />
        </>
    )
}
